import { useEffect, useState } from 'react'
import {
  Typography,
  Box,
  Paper,
  Stack,
  TextField,
  IconButton,
  Chip,
  Button,
  Tooltip,
  Alert,
  Switch
} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortModel
} from '@mui/x-data-grid'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import SyncIcon from '@mui/icons-material/Sync'
import SyncDialog from 'components/SyncDialog'
import {
  getProductSyncJob,
  getProducts,
  syncProducts,
  Product,
  SyncProductsResponse,
  updateProductStatus
} from 'api/products'

type ProductGridRow = Product & {
  collectionName: string
  colorName: string
  categoryName: string
  productTypeName: string
}

const PRODUCT_SYNC_POLL_INTERVAL_MS = 2500
const PRODUCT_SYNC_MAX_POLLS = 240

const delay = (milliseconds: number) =>
  new Promise(resolve => setTimeout(resolve, milliseconds))

const CatalogTab = () => {
  const [products, setProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(25)
  const [searchInput, setSearchInput] = useState('')
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'itemName', sort: 'asc' }
  ])

  const [isSyncingProducts, setIsSyncingProducts] = useState(false)
  const [productDialogOpen, setProductDialogOpen] = useState(false)
  const [productSyncError, setProductSyncError] = useState<string | null>(null)
  const [productSyncResult, setProductSyncResult] = useState<any[] | null>(null)
  const [productUpdatedCount, setProductUpdatedCount] = useState(0)
  const [productCreatedCount, setProductCreatedCount] = useState(0)
  const [productLookupDataSync, setProductLookupDataSync] = useState<
    SyncProductsResponse['productLookupDataSync'] | null
  >(null)
  const [updatingProductId, setUpdatingProductId] = useState<string | null>(
    null
  )

  const fetchProducts = async (
    page: number,
    pageLimit: number,
    keyword?: string,
    nextSortModel: GridSortModel = sortModel
  ) => {
    const activeSort = nextSortModel[0]
    setIsLoading(true)
    setError(null)
    try {
      const response = await getProducts({
        page,
        pageSize: pageLimit,
        keyword: keyword || undefined,
        includeInactive: true,
        includeKitComponents: true,
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort || undefined
      })
      setProducts(response.products || [])
      setTotalRecords(response.totalItems || 0)
      setCurrentPage(response.currentPage || 1)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch products')
      setProducts([])
      setTotalRecords(0)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchProducts(currentPage, limit)
  }, [])

  const handleSearch = () => fetchProducts(1, limit, searchInput)
  const handleClearSearch = () => {
    setSearchInput('')
    fetchProducts(1, limit, '')
  }

  const formatPrice = (price: number | null) =>
    price === null ? 'N/A' : `$${price.toFixed(2)}`

  const booleanChip = (
    value: boolean | null,
    trueLabel = 'Yes',
    falseLabel = 'No'
  ) => {
    if (value === null) return <Chip label='N/A' size='small' />
    return value ? (
      <Chip label={trueLabel} size='small' color='success' />
    ) : (
      <Chip label={falseLabel} size='small' color='default' />
    )
  }

  const nullableChip = (value: string | null | undefined, maxWidth: number) =>
    value ? (
      <Chip label={value} size='small' variant='outlined' sx={{ maxWidth }} />
    ) : (
      <Typography variant='body2' color='text.secondary'>
        —
      </Typography>
    )

  const updateProductRow = (updated: Product) => {
    setProducts(prev =>
      prev.map(row =>
        row.id === updated.id
          ? {
              ...row,
              isActive: updated.isActive,
              isDiscountable: updated.isDiscountable
            }
          : row
      )
    )
  }

  const handleToggleProductFlag = async (
    product: Product,
    field: 'isActive' | 'isDiscountable'
  ) => {
    setUpdatingProductId(product.id)
    setError(null)
    try {
      const updated = await updateProductStatus(product.id, {
        [field]: product[field] !== true
      })
      updateProductRow(updated)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update product status'
      )
    } finally {
      setUpdatingProductId(null)
    }
  }

  const renderProductFlagSwitch = (
    params: GridRenderCellParams<ProductGridRow>,
    field: 'isActive' | 'isDiscountable',
    checked: boolean,
    label: string
  ) => (
    <Switch
      size='small'
      checked={checked}
      disabled={updatingProductId === params.row.id}
      onChange={() => handleToggleProductFlag(params.row, field)}
      inputProps={{
        'aria-label': `Set ${params.row.itemName} ${label} status`
      }}
    />
  )

  const handleSyncProducts = async () => {
    setProductSyncError(null)
    setProductDialogOpen(true)
    setIsSyncingProducts(true)
    setProductSyncResult(null)
    setProductUpdatedCount(0)
    setProductCreatedCount(0)
    setProductLookupDataSync(null)
    try {
      const startedJob = await syncProducts()
      let completedJob = startedJob

      for (let pollCount = 0; pollCount < PRODUCT_SYNC_MAX_POLLS; pollCount++) {
        if (completedJob.status !== 'running') break
        await delay(PRODUCT_SYNC_POLL_INTERVAL_MS)
        completedJob = await getProductSyncJob(startedJob.id)
      }

      if (completedJob.status === 'running') {
        throw new Error(
          'Product sync is still running. Close this dialog and check again later.'
        )
      }

      if (completedJob.status === 'failed') {
        throw new Error(completedJob.error || 'Failed to sync products')
      }

      if (!completedJob.result) {
        throw new Error('Product sync completed without a result.')
      }

      const result = completedJob.result
      setProductSyncResult(result.updates ?? [])
      setProductUpdatedCount(result.updatedCount ?? 0)
      setProductCreatedCount(result.createdCount ?? 0)
      setProductLookupDataSync(result.productLookupDataSync ?? null)
      await fetchProducts(currentPage, limit, searchInput)
    } catch (err: any) {
      setProductSyncError(
        err.response?.data?.error || err.message || 'Failed to sync products'
      )
    } finally {
      setIsSyncingProducts(false)
    }
  }

  const productRows: ProductGridRow[] = products.map(product => ({
    ...product,
    collectionName: product.collection?.name || '',
    colorName: product.color?.name || '',
    categoryName: product.category?.name || '',
    productTypeName: product.productType?.name || ''
  }))

  const productColumns: GridColDef<ProductGridRow>[] = [
    {
      field: 'itemName',
      headerName: 'SKU',
      width: 130,
      renderCell: params => (
        <Typography variant='body2' fontWeight='medium'>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'itemType',
      headerName: 'Type',
      width: 150,
      renderCell: params => (
        <Chip
          label={params.value || 'N/A'}
          size='small'
          color='primary'
          variant='outlined'
        />
      )
    },
    {
      field: 'collectionName',
      headerName: 'Collection',
      width: 230,
      renderCell: params => nullableChip(params.value, 220)
    },
    {
      field: 'colorName',
      headerName: 'Color',
      width: 180,
      renderCell: params => nullableChip(params.value, 170)
    },
    { field: 'categoryName', headerName: 'Category', width: 150 },
    { field: 'productTypeName', headerName: 'Product Type', width: 220 },
    {
      field: 'description',
      headerName: 'Description',
      width: 340,
      renderCell: params => (
        <Typography
          variant='body2'
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={params.value || ''}
        >
          {params.value || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'stock',
      headerName: 'Stock',
      type: 'number',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: params => (
        <Chip
          label={params.value}
          size='small'
          color={Number(params.value) > 0 ? 'success' : 'primary'}
        />
      )
    },
    {
      field: 'basePrice',
      headerName: 'Base Price',
      type: 'number',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: value => formatPrice(value as number | null)
    },
    {
      field: 'weight',
      headerName: 'Weight (lbs)',
      type: 'number',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: value => (value === null ? 'N/A' : String(value))
    },
    {
      field: 'isActive',
      headerName: 'Active',
      type: 'boolean',
      width: 100,
      sortable: true,
      renderCell: (params: GridRenderCellParams<ProductGridRow>) =>
        renderProductFlagSwitch(
          params,
          'isActive',
          params.row.isActive === true,
          'active'
        )
    },
    {
      field: 'componentOfKitPackageItem',
      headerName: 'Kit Component',
      type: 'boolean',
      width: 140,
      renderCell: params =>
        booleanChip(params.row.componentOfKitPackageItem, 'Kit part', 'No')
    },
    {
      field: 'isDiscountable',
      headerName: 'Discountable',
      type: 'boolean',
      width: 130,
      sortable: true,
      renderCell: (params: GridRenderCellParams<ProductGridRow>) =>
        renderProductFlagSwitch(
          params,
          'isDiscountable',
          params.row.isDiscountable !== false,
          'discountable'
        )
    },
    {
      field: 'canBeAssembled',
      headerName: 'Assembly',
      type: 'boolean',
      width: 110,
      renderCell: params => booleanChip(params.row.canBeAssembled)
    },
    {
      field: 'canBeHinged',
      headerName: 'Hinge',
      type: 'boolean',
      width: 100,
      renderCell: params => booleanChip(params.row.canBeHinged)
    }
  ]

  return (
    <>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}
      >
        <TextField
          sx={{ minWidth: 400, flexGrow: 1, maxWidth: 600 }}
          size='small'
          label='Search by SKU or Description'
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') handleSearch()
          }}
          InputProps={{
            endAdornment: (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {searchInput && (
                  <IconButton
                    size='small'
                    onClick={handleClearSearch}
                    sx={{ color: 'text.secondary' }}
                  >
                    <ClearIcon fontSize='small' />
                  </IconButton>
                )}
                <IconButton size='small' onClick={handleSearch} color='primary'>
                  <SearchIcon fontSize='small' />
                </IconButton>
              </Box>
            )
          }}
        />

        <Stack direction='row' spacing={1}>
          <Tooltip title='Sync collections, colors, product taxonomy, stock, price, dimensions, and all NetSuite products.'>
            <span>
              <Button
                variant='outlined'
                startIcon={
                  isSyncingProducts ? null : <SyncIcon fontSize='small' />
                }
                onClick={handleSyncProducts}
                disabled={isSyncingProducts}
                sx={{ minWidth: 200 }}
              >
                Sync NetSuite Products
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant='outlined'>
        <DataGrid
          rows={productRows}
          columns={productColumns}
          loading={isLoading}
          rowCount={totalRecords}
          paginationMode='server'
          paginationModel={{ page: currentPage - 1, pageSize: limit }}
          onPaginationModelChange={model => {
            setLimit(model.pageSize)
            fetchProducts(model.page + 1, model.pageSize, searchInput)
          }}
          sortingMode='server'
          sortModel={sortModel}
          onSortModelChange={model => {
            setSortModel(model)
            fetchProducts(1, limit, searchInput, model)
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          getRowClassName={params =>
            params.row.isActive === false ? 'inactive-product-row' : ''
          }
          sx={{
            minHeight: 620,
            border: 0,
            '& .inactive-product-row': {
              opacity: 0.6
            }
          }}
        />
      </Paper>

      <SyncDialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        title='Sync Products'
        isLoading={isSyncingProducts}
        error={productSyncError}
        syncResult={productSyncResult}
        updatedCount={productUpdatedCount}
        createdCount={productCreatedCount}
        productLookupDataSync={productLookupDataSync}
        resultType='product'
      />
    </>
  )
}

export default CatalogTab
